import { Injectable } from '@angular/core';
import { GenderTypes } from 'src/app/feed-advice/models/feed-advice.model';
import { Discount } from 'src/app/settings/models/settings.model';
import { FEED_PROFIT_ENERGY_VALUES } from 'src/assets/data/feedProfitConstants';

import {
  Calculation,
  CountryOptions,
  ExtendedSimulation,
  ExtendedSimulationFullObject,
  Feeder,
  FeederType,
  GenderOptions,
  QuickSimulation,
} from '../models/feed-profit.model';

@Injectable({
  providedIn: 'root',
})
export class FeedProfitCalculations {
  constructor() {}
  //excel rounds to 8 after comma, need to account for this since calculations are slightly off without it
  roundToExcelStandards(input: number) {
    return Math.round(input * 100000000) / 100000000;
  }

  toExcelPercentage(input: number) {
    //percentage as in 96.3 to percentage as used by spreadsheet (0.963)
    return input * 0.01;
  }

  toVisualPercentage(input: number) {
    return input * 100;
  }

  // Calculates H51
  getSpecificFeederFeedPrice(calculation: Calculation, feedType: FeederType) {
    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    const feedersOfType = allFeeders.filter((feeder) => feeder.type === feedType);
    let feedExpenses = 0;
    let feedKg = 0;
    feedersOfType
      .map((feeder) => {
        const feederFeed =
          feeder.genderFeedRatios.map((ratio) => ratio.feed).reduce((prev, cur) => prev + cur, 0) /
          feeder.genderFeedRatios.length;

        return { price: feederFeed * feeder.product.grossPricePer100Kg * 0.01, kg: feederFeed };
      })
      .forEach((feeder) => {
        feedExpenses += feeder.price;
        feedKg += feeder.kg;
      });

    return (feedExpenses / feedKg) * 100;
  }

  // Calculates J27
  getEffectOnFeedCostsSlaughteredWeightHas(
    calculation: Calculation,
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
  ) {
    const endFeederPrice = this.getSpecificFeederFeedPrice(calculation, FeederType.END_FEEDER); //H53
    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    const endFeeders = allFeeders.filter((feeder) => feeder.type === FeederType.END_FEEDER);
    const avgEnergyEndFeeders =
      endFeeders.map((feeder) => feeder.product.ewKg).reduce((prev, cur) => prev + cur, 0) /
      endFeeders.length;
    const divergingEnergyEndWeight = this.getDivergingEnergyFromGrowthEndDeliveredWeight(
      calculation,
      simulation,
    );

    return ((endFeederPrice / 100) * divergingEnergyEndWeight) / avgEnergyEndFeeders;
  }

  //generic
  getDivergingEnergyFromGrowthStartStartWeight(
    calculation: Calculation,
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
  ) {
    const energyConversion = this.getEnergyConvertedBeginProcedure(calculation, simulation);

    return simulation.startWeight < calculation.extendedBaseLine.startWeight
      ? (simulation.startWeight - calculation.extendedBaseLine.startWeight) * energyConversion * -1
      : (calculation.extendedBaseLine.startWeight - simulation.startWeight) * energyConversion;
  }

  //generic
  getDivergingEnergyFromGrowthEndDeliveredWeight(
    calculation: Calculation,
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
    livingWeight?: number,
    calculationLivingWeight?: number,
  ) {
    livingWeight = livingWeight ?? this.getLivingWeight(calculation, simulation);

    calculationLivingWeight =
      calculationLivingWeight ?? this.getCalculationLivingWeight(calculation);
    const energyConversion = this.getEnergyConvertedEndProcedure(calculation);

    const energyDivergence =
      calculation.genders === GenderOptions.BOAR_GILT && calculation.country === CountryOptions.NL
        ? (livingWeight - calculationLivingWeight) * energyConversion
        : calculation.genders === GenderOptions.BARROW_GILT &&
            calculation.country === CountryOptions.NL
          ? (livingWeight - calculationLivingWeight) * energyConversion
          : (livingWeight - calculationLivingWeight) * energyConversion;
    return energyDivergence;
  }

  //generic
  getEnergyValueFromLookup(value: number, returnValues: number[]) {
    for (let i = 0; i < FEED_PROFIT_ENERGY_VALUES.lowerbounds.length; i++) {
      if (
        value >= FEED_PROFIT_ENERGY_VALUES.lowerbounds[i] &&
        value <= FEED_PROFIT_ENERGY_VALUES.upperbounds[i]
      ) {
        return returnValues[i];
      }
    }
    return 0;
  }

  getEnergyConvertedBeginProcedure(
    calculation: Calculation,
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
  ) {
    const averageEnergyConversion = calculation.extendedBaseLine.averageEnergyConversion;

    return simulation.startWeight > 25
      ? this.getEnergyValueFromLookup(
          averageEnergyConversion,
          FEED_PROFIT_ENERGY_VALUES.values.startMoreThan25,
        )
      : this.getEnergyValueFromLookup(
          averageEnergyConversion,
          FEED_PROFIT_ENERGY_VALUES.values.startLessThan25,
        );
  }

  getEnergyConvertedEndProcedure(calculation: Calculation) {
    const averageEnergyConversion = calculation.extendedBaseLine.averageEnergyConversion;
    let energyConversionFromTable = this.getEnergyValueFromLookup(
      averageEnergyConversion,
      FEED_PROFIT_ENERGY_VALUES.values.endBoar,
    );

    if (calculation.genders === GenderOptions.BARROW_GILT) {
      energyConversionFromTable += 0.2;
    }
    return energyConversionFromTable;
  }
  // Calculates J11
  //generic
  getLivingWeight(
    calculation: Calculation,
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
  ) {
    return simulation.slaughteredWeightInKg * this.getGenderedBasedMultiplier(calculation) + 5;
  }

  // Calculates F11
  //generic
  getCalculationLivingWeight(calculation: Calculation) {
    return (
      calculation?.extendedBaseLine?.slaughteredWeightInKg *
        this.getGenderedBasedMultiplier(calculation) +
      5
    );
  }
  // Calculates J12
  // More / less diverging days start weight.
  getDivergingDaysStartWeight(
    calculation: Calculation,
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
    comparisonSimulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
  ) {
    const divergingDaysStartWeight =
      simulation.startWeight < comparisonSimulation.startWeight
        ? ((simulation.startWeight - comparisonSimulation.startWeight) /
            (calculation.baseLine.growthStartProcedure * 0.001)) *
          -1
        : (comparisonSimulation.startWeight - simulation.startWeight) /
          (calculation.baseLine.growthStartProcedure * 0.001);
    return divergingDaysStartWeight;
  }
  // Calculates J13
  getDivergingDaysSlaughteredWeight(
    calculation: Calculation,
    livingWeight: number,
    calculationLivingWeight: number,
  ) {
    const divergingDaysSlaughteredWeight =
      Math.abs(livingWeight - calculationLivingWeight) /
      (calculation.baseLine.growthEndProcedure * 0.001);
    return divergingDaysSlaughteredWeight;
  }

  // generic
  getGenderedBasedMultiplier(calculation: Calculation) {
    let genderBasedMultiplier = 1;
    if (
      calculation.genders === GenderOptions.BOAR_GILT &&
      calculation.country === CountryOptions.NL
    ) {
      genderBasedMultiplier = 1.21;
    } else if (
      calculation.genders === GenderOptions.BARROW_GILT &&
      calculation.country === CountryOptions.NL
    ) {
      genderBasedMultiplier = 1.2;
    } else if (
      calculation.genders === GenderOptions.BOAR_GILT &&
      calculation.country === CountryOptions.DU
    ) {
      genderBasedMultiplier = 1.225;
    } else if (
      calculation.genders === GenderOptions.BARROW_GILT &&
      calculation.country === CountryOptions.DU
    ) {
      genderBasedMultiplier = 1.215;
    } else if (
      calculation.genders === GenderOptions.BARROW_GILT &&
      calculation.country === CountryOptions.BIO
    ) {
      genderBasedMultiplier = 1.235;
    }
    return genderBasedMultiplier;
  }

  // Calculates T53
  //generic
  getFeedConversion(
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
    PigFeedSum: number,
    livingWeight: number,
  ) {
    return PigFeedSum / (livingWeight - simulation.startWeight);
  }

  // Calculates J29
  getMaxEnergy(calculation: Calculation) {
    let maxEnergy = 0;
    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    allFeeders
      .map((feeder) => {
        const feederFeed = feeder.genderFeedRatios
          .map((ratio) => ratio.feed)
          .reduce((prev, cur) => prev + cur, 0);

        return {
          product: feeder.product,
          averageKg: feederFeed / feeder.genderFeedRatios.length,
        };
      })
      .forEach((feeder) => (maxEnergy += feeder.product.ewKg * feeder.averageKg));
    return maxEnergy;
  }

  getFeedOfGrowFinisherGender(
    calculation: Calculation,
    genderType: GenderTypes,
    feeders: Feeder[] = calculation.feeders,
  ) {
    let feedOfGrowFinisherGender = 0;

    const mappedFeeders = feeders.map((feeder) => {
      const ratio = feeder.genderFeedRatios.filter((ratio) => ratio.name === genderType)[0];
      return (ratio?.feed || 0) * feeder.product.ewKg;
    });
    mappedFeeders.forEach((feeder) => (feedOfGrowFinisherGender += feeder));
    if (!feedOfGrowFinisherGender) {
      feedOfGrowFinisherGender = 0;
    }
    return feedOfGrowFinisherGender;
  }

  // Calculates J25
  // generic
  getPigletPrice(
    calculation: Calculation,
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
  ) {
    return simulation.revenuePerKg * simulation.slaughteredWeightInKg - simulation.startWeight < 25
      ? simulation.pigletPrice -
          (25 - simulation.startWeight) * calculation.baseLine.calculatedPriceLessThan25Kg
      : simulation.pigletPrice +
          (simulation.startWeight - 25) * calculation.baseLine.calculatedPriceLargerThan25Kg;
  }

  getProfitGrowFinisherCalculation(calculation: Calculation) {
    return (
      calculation.extendedBaseLine.revenuePerKg * calculation.extendedBaseLine.slaughteredWeightInKg
    );
  }

  // generic
  getProfitGrowFinisher(
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
  ) {
    return simulation.revenuePerKg * simulation.slaughteredWeightInKg;
  }

  // Calculates S50
  getPigFeedSum(
    calculation: Calculation,
    simulation: QuickSimulation | ExtendedSimulation | ExtendedSimulationFullObject,
    isComparison = false,
    livingWeight?: number,
  ) {
    const divergingEnergyStart = this.getDivergingEnergyFromGrowthStartStartWeight(
      calculation,
      simulation,
      //calculation.comparisonSimulation,
    );
    const divergingEnergyEnd = this.getDivergingEnergyFromGrowthEndDeliveredWeight(
      calculation,
      simulation,
      livingWeight,
    );
    let PigFeedSum = 0;
    const allFeeders = [...calculation.feeders, calculation.calculatedFeeder];

    if (!isComparison) {
      allFeeders
        .map((feeder) => {
          const feederFeed = feeder.genderFeedRatios
            .map((ratio) => ratio.feed)
            .reduce((prev, cur) => prev + cur, 0);

          let returnFeed = 0;
          switch (feeder.type) {
            case FeederType.START_FEEDER:
              returnFeed = feederFeed / feeder.genderFeedRatios.length + divergingEnergyStart;
              break;
            case FeederType.GROWTH_FEEDER:
              returnFeed = feederFeed / feeder.genderFeedRatios.length;
              break;

            case FeederType.END_FEEDER:
              returnFeed = feederFeed / feeder.genderFeedRatios.length + divergingEnergyEnd;
              break;
            default:
              break;
          }
          return returnFeed;
        })
        .forEach((feederAvg) => (PigFeedSum += feederAvg ?? 0));
    } else {
      allFeeders
        .map((feeder) =>
          feeder.genderFeedRatios.map((ratio) => ratio.feed).reduce((prev, cur) => prev + cur, 0),
        )
        .forEach((feed) => (PigFeedSum += feed ?? 0));
    }
    return PigFeedSum ?? 0;
  }

  getProductPriceWithDiscounts(price: number, discounts: Discount) {
    if (!discounts) {
      return price;
    }
    let discountedPrice = price;
    if (!discountedPrice) {
      return discountedPrice;
    }
    discountedPrice =
      (discountedPrice -
        (discounts.bulkDiscountPer100Kg || 0) -
        (discounts.purchaseDiscountPer100Kg || 0) -
        (discounts.deliveryDayDiscountPer100Kg || 0)) *
        (1 - (discounts.paymentDiscountPercentage / 100 || 0)) *
        (1 - (discounts.memberBenefitDiscountPercentage / 100 || 0)) -
      (discounts.memberDiscountPer100Kg || 0);
    return discountedPrice;
  }

  getCalculatedFeedersWithDiscounts(feeders: Feeder[], discounts: Discount): Feeder[] {
    if (!feeders) {
      return [];
    }
    if (!discounts) {
      return feeders;
    }
    return feeders.map(
      (feeder) =>
        <Feeder>{
          ...feeder,
          product: {
            ...feeder.product,
            price: this.getProductPriceWithDiscounts(feeder.product.grossPricePer100Kg, discounts),
          },
        },
    );
  }

  //check if we can calculate
  getQuickSimulationCanBeCalculated = (calculation: Calculation) =>
    calculation &&
    calculation.genders &&
    calculation.country &&
    calculation.feeders != null &&
    calculation.feeders.length > 0 &&
    calculation?.calculatedFeeder != null &&
    calculation.companyDetails != null &&
    calculation.companyDetails.numberOfSilos != null &&
    calculation.baseLine != null &&
    Object.values(calculation.baseLine).every((val) => val != null) &&
    calculation.extendedBaseLine != null &&
    Object.values(calculation.extendedBaseLine).every((val) => val != null);

  getExtendedSimulationCanBeCalculated = (calculation: Calculation) =>
    this.getQuickSimulationCanBeCalculated(calculation) &&
    this.getExtendedComparisonCanBeCalculated(calculation) &&
    calculation?.extendedSimulation?.feeders != null &&
    calculation?.extendedSimulation?.feeders.length > 0 &&
    calculation?.extendedSimulation?.calculatedFeeder != null &&
    calculation?.extendedSimulation?.baseLine != null &&
    calculation?.extendedSimulation?.extendedBaseLine != null &&
    Object.entries(calculation?.extendedSimulation?.extendedBaseLine).every((entry) =>
      entry[0] === 'feedPackagePrice' ? true : entry[1] != null,
    );

  getExtendedComparisonCanBeCalculated = (calculation: Calculation) =>
    this.getQuickSimulationCanBeCalculated(calculation) &&
    calculation?.extendedComparisonSimulation?.feeders != null &&
    calculation?.extendedComparisonSimulation?.feeders.length > 0 &&
    calculation?.extendedComparisonSimulation?.calculatedFeeder != null &&
    calculation?.extendedComparisonSimulation?.baseLine != null &&
    calculation?.extendedComparisonSimulation?.extendedBaseLine != null &&
    Object.entries(calculation?.extendedComparisonSimulation?.extendedBaseLine).every((entry) =>
      entry[0] === 'feedPackagePrice' ? true : entry[1] != null,
    );

  getHasExtendedSimulation = (calculation: Calculation) => calculation?.extendedSimulation != null;
}
